<script setup>
import {nextTick, onMounted, ref} from 'vue';
import {Chart, registerables} from 'chart.js';
import {useEPStore} from 'stores/ep';
import OpeningOddView from './partials/OpeningOddView';
import router from '../router';

Chart.register(...registerables);

const EPStore = useEPStore();

const data = ref(null);
const input = ref(null);
const myChart = ref(null);
const chartData = ref([]);
const copyUrlButton = ref(null);

const hoveredKey = ref(null);

// A move of the net payout this big (in %) counts as a step for the staircase detector.
const PAYOUT_STEP = 6;
const isBigScreen = ref(window.innerWidth > 768);

onMounted(() => {
	const url = router.currentRoute.value.query.url;
	const type = router.currentRoute.value.query.type ?? 'negative';

	if (url) {
		input.value.value = url;
		loadData(url, type);
	}

	window.addEventListener('resize', handleResize);
});

function handleResize() {
	isBigScreen.value = window.innerWidth > 768;
}

function loadData(url, type = 'negative') {
	data.value = null;
	document.title = 'EP';

	EPStore.analyze(url, type).then(response => {
		if (response && response.data.success) {
			document.title = `${response.data.data.eventInfo.teams.home.name} - ${response.data.data.eventInfo.teams.away.name}`;
			data.value = response.data.data;
			chartData.value = getHandledPercentageMovements();
			router.replace({query: {...router.currentRoute.value.query, url, type}});
			createChart();
		}
	});
}

function handleSubmit(event) {
	event.preventDefault();
	const type = router.currentRoute.value.query.type ?? 'negative';

	loadData(input.value.value, type);
}

function handleInputFocus() {
	input.value.value = null;
}

function getHandledPercentageMovements() {
	return Object.values(data.value.percentageMovements).map(item => {
		return {
			x: item['datetime'],
			y: item['odd'],
			movement: item['movement'],
			payoutMovement: item['payoutMovement'],
			timestamp: item['timestamp']
		};
	});
}

async function copyUrl() {
	copyUrlButton.value.disabled = true;
	await navigator.clipboard.writeText(window.location.href);

	const oldHTML = copyUrlButton.value.innerHTML;
	copyUrlButton.value.innerHTML = '<i class="fa-solid fa-check text-primary"></i>';

	setTimeout(() => {
		copyUrlButton.value.innerHTML = oldHTML;
		copyUrlButton.value.disabled = false;
	}, 500);
}

function redirectToOddsPortal() {
	window.open(router.currentRoute.value.query.url, '_blank');
}

async function createChart() {
	await nextTick();
	const ctx = myChart.value.getContext('2d');

	const dataset = chartData.value;
	if (!dataset.length) return;

	const firstPoint = dataset[0];
	const lastPoint = dataset[dataset.length - 1];

	new Chart(ctx, {
		type: 'line',
		data: {
			datasets: [{
				label: 'Koeficientas',
				data: dataset,
				borderColor: 'rgb(255, 99, 132)',
				pointRadius: 2,
				pointBorderColor: 'rgb(121,121,121)',
				pointBackgroundColor: 'white',
				segment: {
					borderColor: ctx => {
						if (ctx.p0 && Math.abs(ctx.p0.raw.payoutMovement) >= PAYOUT_STEP) {
							return 'rgb(99,148,255)';
						}
					}
				}
			}]
		},
		options: {
			responsive: true,
			plugins: {
				title: {display: false},
				legend: {display: false},
				tooltip: {
					callbacks: {
						label(context) {
							let value = context.parsed.y.toFixed(2);
							return `Koeficientas: ${value}`;
						},
						afterBody(context) {
							const move = context[0]?.raw?.payoutMovement;
							return move ? `Pokytis: ${move.toFixed(2)}%` : '';
						}
					},
					displayColors: false
				}
			},
			scales: {
				y: {
					beginAtZero: false,
					ticks: {
						callback: v => v.toFixed(2),
						maxTicksLimit: 20
					}
				},
				x: {reverse: true, ticks: {display: false}}
			}
		},
		plugins: [{
			id: 'trendLine',
			afterDraw(chart) {
				const {ctx, chartArea: {left, right, top, bottom}, scales: {x, y}} = chart;

				if (!x || !y) return;

				// Tiesės koordinatės grafike
				const x1 = x.getPixelForValue(firstPoint.x);
				const y1 = y.getPixelForValue(firstPoint.y);
				const x2 = x.getPixelForValue(lastPoint.x);
				const y2 = y.getPixelForValue(lastPoint.y);

				ctx.save();
				ctx.beginPath();
				ctx.moveTo(x1, y1);
				ctx.lineTo(x2, y2);
				ctx.lineWidth = 2;
				ctx.strokeStyle = 'rgba(0, 150, 255, 0.7)'; // mėlyna tiesė
				ctx.setLineDash([6, 4]); // punktyrinė linija
				ctx.stroke();
				ctx.restore();
			}
		}]
	});
}

function getColorByTimestamp(timestamp) {
	if (data.value.coloringTimestamps.negative.find(item => item === timestamp)) {
		return 'bg-row-red';
	} else if (data.value.coloringTimestamps.positive.find(item => item === timestamp)) {
		return 'bg-row-green';
	}

	return null;
}
</script>
<template>
	<div class="d-flex flex-column align-items-center gap-4 w-100">
		<div class="col-12 col-lg-8 col-xl-7 d-flex flex-column align-items-center justify-content-center gap-3">
			<form class="input-group" @submit="handleSubmit">
				<button v-if="data" ref="copyUrlButton" class="btn btn-copy fs-7" type="button" @click="copyUrl"><i
					class="fa-regular fa-copy text-dark"></i></button>
				<input ref="input" class="form-control fs-7 px-3" placeholder="URL" required
					   type="text" @focus="handleInputFocus">
				<button class="btn btn-dark fs-7 px-3" type="submit">Tikrinti</button>
			</form>
		</div>
		<div v-if="data" class="row g-4">
			<div class="col-12 d-flex flex-column gap-3">
				<div v-if="data.eventInfo.foundedBet" class="text-center text-secondary"><i
					class="fa-solid fa-circle-check me-1 text-warning"></i>Šios rungtynės atitinka algoritmą (<span
					class="fw-bold">{{ data.eventInfo.foundedBet.difference }}</span>)
				</div>
				<div class="d-flex justify-content-center gap-3">
					<div class="d-flex justify-content-center gap-2 text-center">
						<div class="d-flex gap-2">
							<div class="d-flex flex-column align-items-center gap-1">
								<span
									:class="['fs-5 team-title-analyzer fw-bold', data.eventInfo.biggerOddsTeam === 'H' ? 'text-red' : 'text-green']">{{
										data.eventInfo.teams.home.name
									}}</span>
								<span class="fs-8">{{ data.eventInfo.teams.home.endOdd.odd.toFixed(2) }}</span>
							</div>
						</div>
						<div class="d-flex mx-2 align-items-start">
							<span class="fw-bold fs-5 teams-separator">-</span>
						</div>
						<div class="d-flex gap-2">
							<div class="d-flex flex-column align-items-center gap-1">
								<span
									:class="['fs-5 team-title-analyzer fw-bold', data.eventInfo.biggerOddsTeam === 'A' ? 'text-red' : 'text-green']">{{
										data.eventInfo.teams.away.name
									}}</span>
								<span class="fs-8">{{ data.eventInfo.teams.away.endOdd.odd.toFixed(2) }}</span>
							</div>
						</div>
					</div>
				</div>
				<div v-if="data.eventInfo.handicap" class="text-center">
					Prognozė:
					<span class="fw-bold">{{ data.eventInfo.teams.biggerOddsTeam.name }} {{
							data.eventInfo.handicap.line > 0 ? '+' : ''
						}}{{ data.eventInfo.handicap.line }}</span>
					<span class="text-secondary">(≈{{ data.eventInfo.handicap.odd.toFixed(2) }})</span>
				</div>
				<!--				<div v-if="!data.event_info.live && data.event_info.finished" class="text-center">-->
				<!--					<span>Rungtynės baigėsi rezultatu <span class="fw-bold">{{-->
				<!--							data.event_info.home_team.result-->
				<!--						}}:{{ data.event_info.away_team.result }}</span></span>-->
				<!--				</div>-->
				<div class="d-flex flex-column align-items-center gap-2">
					<div class="d-flex justify-content-center align-items-center gap-2">
						<!--						<i v-if="data.event_info.live" class="fa-solid fa-circle-dot fa-beat-fade text-danger"></i>-->
						<span class="text-center">{{ data.eventInfo.eventTime }}</span>
					</div>
					<span class="text-center text-secondary">{{ data.eventInfo.sport }}</span>
				</div>
				<div class="text-center">
					<button class="btn btn-link text-primary fs-7 p-0" @click="redirectToOddsPortal()">
						Eiti į www.oddsmath.com
					</button>
				</div>
			</div>
			<div v-if="!isBigScreen" class="col-12 col-xl-6 d-flex flex-column align-items-center gap-3">
				<span class="text-secondary" style="letter-spacing: 2px">Koeficientų judėjimas grafiškai</span>
				<canvas ref="myChart"></canvas>
				<OpeningOddView :data="data" class="d-none d-xl-flex"/>
			</div>
			<div class="col-12 col-xl-6 d-flex flex-column align-items-center gap-2">
				<div class="d-flex flex-column flex-md-row gap-2 gap-md-5">
					<ul v-if="isBigScreen" class="d-flex flex-column">
						<li v-for="(item, key) in data.movements" :key="key" class="d-flex align-items-center gap-3">
							<span :class="getColorByTimestamp(item.timestamp)">{{ item.datetime }}</span>
							<span class="fw-bold" style="width: 37px">{{ Math.abs(item.odd).toFixed(2) }}</span>
							<div class="d-flex">
								<span :class="['text-center', item.movement > 0 ? 'text-green' : 'text-red']"
									  style="width: 13px">{{ item.movement > 0 ? '+' : '-' }}</span>
								<span :class="[item.movement > 0 ? 'text-green' : 'text-red']">{{
										Math.abs(item.movement).toFixed(2)
									}}</span>
							</div>
						</li>
					</ul>
					<ul class="d-flex flex-column">
						<li v-for="(item, key) in data.percentageMovements" :key="key"
							class="d-flex align-items-center gap-3">
							<i v-if="hoveredKey === item.timestamp" class="fa-solid fa-circle position-absolute"
							   style="margin-left: -15px; font-size: 6px"></i>
							<span :class="getColorByTimestamp(item.timestamp)">{{ item.datetime }}</span>
							<span class="fw-bold" style="width: 37px">{{ Math.abs(item.odd).toFixed(2) }}</span>
							<!-- The change of the net payout (odd - 1): comparable at any odd. -->
							<div class="d-flex" style="width: 70px">
								<span :class="['text-center', item.payoutMovement > 0 ? 'text-green' : 'text-red']"
									  style="width: 13px">{{ item.payoutMovement > 0 ? '+' : '-' }}</span>
								<span :class="[item.payoutMovement > 0 ? 'text-green' : 'text-red']">{{
										Math.abs(item.payoutMovement).toFixed(2)
									}}</span>
								<span :class="['text-center', item.payoutMovement > 0 ? 'text-green' : 'text-red']"
									  style="width: 13px">%</span>
							</div>
							<i v-if="Math.abs(item.payoutMovement) >= PAYOUT_STEP"
							   :class="['fa-solid fa-triangle-exclamation', item.payoutMovement > 0 ? 'text-green' : 'text-red']"></i>
						</li>
					</ul>
				</div>
				<OpeningOddView :data="data" class="d-flex d-xl-none"/>
			</div>
			<div v-if="isBigScreen" class="col-12 col-xl-6 d-flex flex-column align-items-center gap-3">
				<span class="text-secondary" style="letter-spacing: 2px">Koeficientų judėjimas grafiškai</span>
				<canvas ref="myChart"></canvas>
				<OpeningOddView :data="data" class="d-none d-xl-flex"/>
			</div>
		</div>
	</div>
</template>
